import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';

import type { PageHeaderProps } from './PageHeader.types';

/**
 * PageHeader Component
 *
 * A flexible header component for pages that displays a title,
 * optional subtitle, and an optional action button in the top-right.
 * Follows the pattern from PatientListPage header.
 *
 * @example
 * ```tsx
 * <PageHeader
 *   title="Patients"
 *   subtitle="1,234 patients"
 *   action={<Button>New Patient</Button>}
 * />
 * ```
 */
export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  action,
  sx,
  ...props
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 3,
        flexWrap: 'wrap',
        gap: 2,
        ...sx,
      }}
      {...props}
    >
      {title && (
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.25 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      )}
      {action && <Box>{action}</Box>}
    </Box>
  );
};
